import type { ComponentProps, ReactNode } from "react";
import { Switch } from "rizzui";
import { cn } from "@steelyard/theme";

/**
 * A record's Active/Inactive status inside its edit form (2026-09-29, the project owner's request —
 * the checkbox card read "Active" whether ticked or not). A switch, as the template's own forms
 * toggle a setting — COPIED from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/shipping-info.tsx's
 * `Switch` — labelled with the current state in words, and a helper line saying what that state
 * means. This is the one place an edit screen shows the status: no badge beside it, and none in
 * the page's `RecordSummary` strip.
 *
 * The state word is CSS-driven off the switch's own `:checked` (`group-has-[:checked]`), so it
 * stays a drop-in for `{...register("isActive")}` with no `watch`, and the hidden word is
 * `display: none` — out of the switch's accessible name, not just invisible.
 */
export function StatusSwitch({
  activeText,
  inactiveText,
  onLabel = "Active",
  offLabel = "Inactive",
  className,
  ...input
}: {
  /** What Active means for this record, shown while it is on. */
  activeText: ReactNode;
  /** What switching it off does, shown while it is off. */
  inactiveText: ReactNode;
  /** The state words, for a setting that is not a record's status (e.g. Enabled/Disabled). */
  onLabel?: string;
  offLabel?: string;
  className?: string;
} & Omit<ComponentProps<typeof Switch>, "label" | "helperText">) {
  return (
    <div className={cn("group", className)}>
      <Switch
        {...input}
        label={
          <>
            <span className="text-gray-900 group-has-[:checked]:hidden">{offLabel}</span>
            <span className="hidden text-gray-900 group-has-[:checked]:inline">{onLabel}</span>
          </>
        }
        labelWeight="medium"
        helperText={
          <>
            <span className="group-has-[:checked]:hidden">{inactiveText}</span>
            <span className="hidden group-has-[:checked]:inline">{activeText}</span>
          </>
        }
        // Under the label, not the knob: the md switch is w-10 and its label sits ms-2 from it.
        helperClassName="ms-12 text-[13px] text-gray-500"
      />
    </div>
  );
}
